// =============================================================================
// CONNECT 2.0 — Project- and site-level bulk action eligibility (CON-154, CON-193).
// -----------------------------------------------------------------------------
// One predicate per bulk button hosted in the banner or a site card. Each
// takes the raw, flat `SiteDetail[]` off a project (`project.raw.sites` in
// `list-grouping.util.ts` terms — `{ site, proposal, reviewStatus }`, kept
// untouched here the same way that module keeps it untouched) and splits it
// into two sets:
//
//  - `inPhase`  — every product sitting in a status this action targets,
//                 regardless of whether it is ready to submit.
//  - `eligible` — the subset of `inPhase` that also clears the action's data
//                 gate (price, and a turn time / delivery date / desired-by
//                 date, depending on the action).
//
// The split exists for the button's disabled-vs-absent gating: a bulk button
// is ABSENT when `inPhase` holds fewer than TWO products, and DISABLED, with a
// tooltip naming what is missing, when `inPhase` holds two or more but
// `eligible` is empty. See docs/connect/2026-09-08-connect-bulk-actions-spec.md.
//
// The two-or-more threshold lives in `project-group.component.html` (the first
// button's `*ngIf`, where its reasoning is documented), NOT here: these
// predicates stay pure set-splitters so the util spec can exercise the phase
// and data gates independently of how the banner chooses to render them. Note
// only that the threshold counts the rows below — site-PRODUCT pairs — so one
// physical site ordered for three services clears it on its own.
//
// `spansOtherSites` compares the project and site phase sets; it does not
// apply the two-or-more threshold.
//
// Pure functions, no Angular dependency, so callers can compute all four
// predicates (plus the payload builder below) off `project.raw.sites` and
// this module stays unit-testable on its own.
// =============================================================================

/**
 * The raw feed row this module reads: `{ site, proposal, reviewStatus }`.
 * Left as `any`-shaped like `list-grouping.util.ts`'s `SiteDetail` — the feed
 * itself is untyped end to end, and this module deliberately passes rows
 * through untouched rather than narrowing them.
 */
export type SiteDetail = any;

/** Two-set eligibility result for one bulk action. */
export interface BulkEligibility {
  /** Every SiteDetail whose proposal sits in this action's target phase. */
  inPhase: SiteDetail[];
  /** The subset of `inPhase` that also clears the action's data gate. */
  eligible: SiteDetail[];
}

export const BULK_SUBMIT_DISABLED_REASON = 'Enter a price and complete-by date on at least one quote to submit all.';
export const BULK_ACCEPT_DISABLED_REASON = 'At least one offer needs a price and a completion date before it can be accepted.';
export const BULK_ATP_DISABLED_REASON = 'No awarded site is ready to authorize yet.';
export const BULK_DECLINE_DISABLED_REASON = 'No product is ready to decline yet.';
/**
 * Why every Sign All ATPs button on a project is disabled while one of them is
 * generating its document or has its sign dialog open. A project hosts one
 * sign dialog, so only one ATP runs at a time.
 */
export const ATP_IN_PROGRESS_REASON = 'Finish the ATP already in progress on this project first.';

/** Where a site's bulk action applies, as its button label and dialogs name it. */
export function siteScopeLabel(siteNumber: number | null | undefined): string {
  return siteNumber == null ? 'at this site' : `at Site ${siteNumber}`;
}

/** `true` when a numeric field is present and greater than zero. */
function isPositive(value: any): boolean {
  return typeof value === 'number' && value > 0;
}

/**
 * Split `siteDetails` into the products at `statuses` (`inPhase`) and the
 * further subset for which `hasData` also holds (`eligible`). Shared plumbing
 * for the four predicates below — coerces a nullish/non-array `siteDetails`
 * to `[]` so a page mid-load (or a project with no sites yet) never throws.
 */
function splitByPhase(
  siteDetails: SiteDetail[] | null | undefined,
  statuses: ReadonlySet<string>,
  hasData: (detail: SiteDetail) => boolean
): BulkEligibility {
  const inPhase = (Array.isArray(siteDetails) ? siteDetails : []).filter(
    (detail) => statuses.has(detail?.proposal?.status)
  );
  return {
    inPhase,
    eligible: inPhase.filter(hasData),
  };
}

/**
 * Submit All Quotes — phase is `INVITATION`. Eligible additionally needs a
 * price AND a completion signal, mirroring `ProposalHandler.submitOwnedBids`
 * (connect-service) and `product-line.component.ts`'s
 * `recomputeSubmitDisabled` (~line 514) exactly: a turn time OR a proposed
 * deliver date OR the site's own `desiredByDate`. This is deliberately NOT
 * the stale 1.0 gate that required a turn time alone — that gap is Defect 2,
 * and the `site.desiredByDate` arm is the regression case for it (a vendor
 * who only ever sees a desired-by date on the quote form could never submit
 * under the old gate).
 */
export function submitAllQuotes(
  siteDetails: SiteDetail[] | null | undefined
): BulkEligibility {
  return splitByPhase(
    siteDetails,
    new Set(['INVITATION']),
    (detail) =>
      isPositive(detail?.proposal?.bid) &&
      (isPositive(detail?.proposal?.proposedTurnTime) ||
        !!detail?.proposal?.proposedDeliverDate ||
        !!detail?.site?.desiredByDate)
  );
}

/**
 * Accept All Offers — phase is `OFFERED` ONLY. `RE_OFFERED` is deliberately
 * excluded, not an oversight: a revised fee is a negotiated figure the PA
 * should accept on the single-line Accept path, where both figures ("Revised
 * offer" / "You asked") are visible, not swept into a bulk accept-all. See
 * `accept-offered-proposal-dialog.component.ts`'s `numberOfAcceptedProposals`
 * input, which states the same exclusion for the count the dialog renders.
 * Eligible additionally
 * needs a price and a completion signal — a turn time or a proposed deliver
 * date (no `desiredByDate` arm here: an offer's due date always arrives as
 * `proposedDeliverDate`, per `product-line`'s `offerDueDate` getter).
 */
export function acceptAllOffers(
  siteDetails: SiteDetail[] | null | undefined
): BulkEligibility {
  return splitByPhase(
    siteDetails,
    new Set(['OFFERED']),
    (detail) =>
      isPositive(detail?.proposal?.bid) &&
      (isPositive(detail?.proposal?.proposedTurnTime) ||
        !!detail?.proposal?.proposedDeliverDate)
  );
}

/**
 * Sign All ATPs — phase is `BID_WON`. Unlike Submit All and Accept All,
 * actions there is no separate data gate: award already implies price and
 * terms are settled, so `inPhase` and `eligible` are identical.
 */
export function signAtpAllAwarded(
  siteDetails: SiteDetail[] | null | undefined
): BulkEligibility {
  return splitByPhase(siteDetails, new Set(['BID_WON']), () => true);
}

/**
 * The statuses a vendor can still decline: Decline All's phase, and what
 * `pendingDeclines` treats as not yet settled after a gateway error.
 */
export const DECLINABLE_STATUSES: ReadonlySet<string> = new Set(['INVITATION', 'OFFERED', 'RE_OFFERED']);

/** Decline All — every invitation or offer can be declined without a data gate. */
export function declineAll(
  siteDetails: SiteDetail[] | null | undefined
): BulkEligibility {
  return splitByPhase(siteDetails, DECLINABLE_STATUSES, () => true);
}

/** Whether the project has in-phase rows beyond those on the given site. */
export function spansOtherSites(
  projectSet: BulkEligibility,
  siteSet: BulkEligibility
): boolean {
  return projectSet.inPhase.length > siteSet.inPhase.length;
}

/**
 * Rebuild the ProjectDetail a bulk write posts: the full raw project (banner-
 * level fields the backend/ATP generator read — projectOrderId, tableA,
 * specialQuoteRequirements, rush, …) with the given N sites, in place of the
 * single site `product-line.component.ts`'s private `buildProjectDetail`
 * builds. Same shape, widened from one site to N, so the reused dialogs and
 * `SiteService`/`ProposalService` calls see exactly what they already expect.
 */
export function buildBulkProjectDetail(
  rawProject: any,
  siteDetails: SiteDetail[]
): any {
  return { ...rawProject, sites: siteDetails };
}
